import { inject, InjectionToken } from '@angular/core';
import { DescMessage, Message, toJson } from '@bufbuild/protobuf';
import { Code, ConnectError, Interceptor, Transport } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { AuthService } from '@pinnakl/auth/providers';
import { ENVIRONMENT } from '@pinnakl/core/environment';
import { firstValueFrom } from 'rxjs';

export const GRPC_TRANSPORT = new InjectionToken<Transport>('GRPC_TRANSPORT', {
  providedIn: 'root',
  factory: () => createTransport(inject(ENVIRONMENT).grpcApiServerUrl ?? ''),
});

export const MARKET_DATA_GRPC_TRANSPORT = new InjectionToken<Transport>('MARKET_DATA_GRPC_TRANSPORT', {
  providedIn: 'root',
  factory: () => {
    const { grpcApiServerUrl, marketDataGrpcApiServerUrl } = inject(ENVIRONMENT);
    return createTransport(marketDataGrpcApiServerUrl ?? grpcApiServerUrl ?? '');
  },
});

function createTransport(baseUrl: string): Transport {
  const interceptors = [authInterceptor(inject(AuthService))];
  if (localStorage.getItem('pinnaklGrpcLogger') === 'true') {
    interceptors.push(grpcLogger);
  }
  return createGrpcWebTransport({ baseUrl, interceptors });
}

function authInterceptor(authService: AuthService): Interceptor {
  return next => async request => {
    if (!request.header.has('Authorization')) {
      request.header.set('Authorization', `Bearer ${await firstValueFrom(authService.getAccessToken())}`);
    }
    try {
      return await next(request);
    } catch (error) {
      // A server stream reports Unauthenticated while it is read, so only unary calls get one retry.
      if (request.stream || !isUnauthenticated(error)) throw error;
      const token = await firstValueFrom(authService.getRefreshedAccessToken()).catch(() => null);
      if (!token) throw error;
      request.header.set('Authorization', `Bearer ${token}`);
      return next(request);
    }
  };
}

function isUnauthenticated(error: unknown): boolean {
  return error instanceof ConnectError && error.code === Code.Unauthenticated;
}

const grpcLogger: Interceptor = next => async request => {
  const name = `${request.service.typeName}/${request.method.name}`;
  if (request.stream === false) {
    log(`[gRPC] → ${name}`, request.method.input, request.message);
  }
  const response = await next(
    request.stream === false
      ? request
      : { ...request, message: logEach(`[gRPC] → ${name}`, request.method.input, request.message) },
  );
  if (response.stream === false) {
    log(`[gRPC] ← ${name}`, response.method.output, response.message);
    return response;
  }
  return { ...response, message: logEach(`[gRPC] ← ${name}`, response.method.output, response.message) };
};

async function* logEach(label: string, schema: DescMessage, messages: AsyncIterable<Message>): AsyncIterable<Message> {
  for await (const message of messages) {
    log(label, schema, message);
    yield message;
  }
}

function log(label: string, schema: DescMessage, message: Message): void {
  try {
    console.info(label, toJson(schema, message));
  } catch {
    console.info(label, message);
  }
}
